// src/pages/BookingDetail.js
import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Paper,
  Box,
  Button,
  IconButton,
  Grid,
  TextField,
  FormControlLabel,
  Checkbox,
  CircularProgress,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import {
  getDoc,
  doc,
  updateDoc,
  deleteDoc,
  arrayUnion,
} from "firebase/firestore";
import { firestore } from "../firebase";
import { getAuth } from "firebase/auth";
import { useAuthState } from "react-firebase-hooks/auth";
import moment from "moment";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { MenuItem } from "@mui/material";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";

const BookingDetail = () => {
  const { bookingId } = useParams(); // Get bookingId from URL parameter
  const auth = getAuth();
  const [user, loadingAuth] = useAuthState(auth);
  const userId = user ? user.uid : null;
  const [shopSlug, setShopSlug] = useState(null);
  const navigate = useNavigate();

  const [booking, setBooking] = useState(null);
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [initialBooking, setInitialBooking] = useState(null);
  // editableBooking state includes the fields we want to edit and a computed totalPrice
  const [editableBooking, setEditableBooking] = useState({
    title: "",
    startDate: "",
    endDate: "",
    dailyPrice: 0,
    baseCost: 0,
    extraHoursCost: 0,
    totalPrice: 0,
    extraHoursRate: 0,
    rentalDays: 0,
    deposit: 0,
    paymentMethod: "",
    paid: false,
    customerFirstName: "",
    customerLastName: "",
    customerEmail: "",
  });
  const [showCancelForm, setShowCancelForm] = useState(false);
  const [cancellationRequestedBy, setCancellationRequestedBy] = useState("");
  const [shouldRefund, setShouldRefund] = useState(false);
  const [cancellationNotes, setCancellationNotes] = useState("");

  const computeDurationDays = (start, end) => {
    const startMs = new Date(start).getTime();
    const endMs = new Date(end).getTime();
    return (endMs - startMs) / (24 * 3600 * 1000);
  };

  const roundToTwo = (num) => Number(Number(num).toFixed(2));

  useEffect(() => {
    if (userId) {
      fetchShopSlug(userId).then((slug) => {
        if (slug) setShopSlug(slug);
      });
    }
  }, [userId]);

  useEffect(() => {
    if (!shopSlug || !bookingId) return;
    const fetchBookingDetails = async () => {
      try {
        const bookingRef = doc(
          firestore,
          "rentalShops",
          shopSlug,
          "bookings",
          bookingId
        );
        const bookingSnap = await getDoc(bookingRef);
        if (bookingSnap.exists()) {
          const bookingData = bookingSnap.data();
          const loaded = {
            title: bookingData.label || "",
            startDate: bookingData.startDate,
            endDate: bookingData.endDate,
            dailyPrice: bookingData.bill?.dailyRate || 0,
            baseCost: bookingData.bill?.baseCost || 0,
            rentalDays: bookingData.bill?.rentalDays || 1,
            extraHoursCost: bookingData.bill?.extraHoursCost || 0,
            extraHoursRate: bookingData.bill?.extraHoursRate || 0,
            totalPrice:
              (bookingData.bill?.baseCost || 0) +
              (bookingData.bill?.extraHoursCost || 0),
            deposit: bookingData.deposit || 0,
            paymentMethod: bookingData.paymentMethod || "",
            paid: bookingData.paid || false,
            customerFirstName: bookingData.customer?.firstName || "",
            customerLastName: bookingData.customer?.lastName || "",
            customerEmail: bookingData.customer?.email || "",
          };
          setBooking({ id: bookingSnap.id, ...bookingData });
          setInitialBooking(loaded);
          setEditableBooking(loaded);

          if (bookingData.vehicle?.vehicleId) {
            const vehicleRef = doc(
              firestore,
              "rentalShops",
              shopSlug,
              "vehicles",
              bookingData.vehicle.vehicleId
            );
            const vehicleSnap = await getDoc(vehicleRef);
            if (vehicleSnap.exists()) {
              setVehicle({ id: vehicleSnap.id, ...vehicleSnap.data() });
            }
          }
        }
      } catch (error) {
        console.error("Error fetching booking details:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchBookingDetails();
  }, [shopSlug, bookingId]);

  const fetchShopSlug = async (userId) => {
    const ownerDocRef = doc(firestore, "users", userId);
    const ownerSnap = await getDoc(ownerDocRef);
    if (ownerSnap.exists()) {
      return ownerSnap.data().shopId;
    } else {
      console.error("No shop assigned to this user.");
      return null;
    }
  };

  const handleCancelEdit = () => {
    setEditableBooking(initialBooking);
    setEditMode(false);
  };

  // Handler for changes in form inputs
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditableBooking((prev) => {
      const updated = { ...prev, [name]: value };

      if (name === "baseCost" || name === "extraHoursCost") {
        updated.totalPrice =
          parseFloat(updated.baseCost) + parseFloat(updated.extraHoursCost);
      }

      if (name === "totalPrice") {
        const newTotal = parseFloat(value);
        const extra = parseFloat(updated.extraHoursCost);
        const base = newTotal - extra;
        const duration = computeDurationDays(
          updated.startDate,
          updated.endDate
        );
        const newDaily = duration > 0 ? base / duration : 0;
        updated.baseCost = roundToTwo(base);
        updated.dailyPrice = roundToTwo(newDaily);
      }

      if (name === "extraHoursCost") {
        const paidExtra = booking?.bill?.paidExtraHours || 0;
        updated.extraHoursRate = paidExtra ? roundToTwo(value / paidExtra) : 0;
        updated.totalPrice = parseFloat(updated.baseCost) + parseFloat(value);
      }

      return updated;
    });
  };

  // Update total when daily changes
  const handleDailyPriceChange = (e) => {
    const newDaily = parseFloat(e.target.value) || 0;
    const rentalDays = editableBooking.rentalDays;
    const base = Number(newDaily) * rentalDays;
    const extra = editableBooking.extraHoursCost || 0;
    setEditableBooking((prev) => ({
      ...prev,
      dailyPrice: newDaily,
      baseCost: base,
      totalPrice: Number(base) + Number(extra),
    }));
  };

  // Update daily when total is changed directly
  const handleTotalPriceChange = (e) => {
    const newTotal = parseFloat(e.target.value) || 0;
    const extra = editableBooking.extraHoursCost || 0;
    const base = newTotal - extra;
    const rentalDays = editableBooking.rentalDays;
    const newDaily = base / rentalDays;
    setEditableBooking((prev) => ({
      ...prev,
      dailyPrice: roundToTwo(newDaily),
      baseCost: roundToTwo(base),
      totalPrice: newTotal,
    }));
  };

  const handleBaseCostPriceChange = (e) => {
    const newBase = parseFloat(e.target.value) || 0;
    const extra = editableBooking.extraHoursCost || 0;
    const rentalDays = editableBooking.rentalDays;
    const newDaily = newBase / rentalDays;
    const newTotal = Number(newBase) + Number(extra);
    setEditableBooking((prev) => ({
      ...prev,
      dailyPrice: roundToTwo(newDaily),
      baseCost: roundToTwo(newBase),
      totalPrice: newTotal,
    }));
  };

  const handleSave = async () => {
    if (!shopSlug || !bookingId) return;
    try {
      const bookingRef = doc(
        firestore,
        "rentalShops",
        shopSlug,
        "bookings",
        bookingId
      );
      const updatedBooking = {
        ...booking,
        startDate: editableBooking.startDate,
        endDate: editableBooking.endDate,
        label: editableBooking.title,
        deposit: Number(editableBooking.deposit),
        paymentMethod: editableBooking.paymentMethod,
        paid: editableBooking.paid,
        customer: {
          firstName: editableBooking.customerFirstName,
          lastName: editableBooking.customerLastName,
          email: editableBooking.customerEmail,
          fullName:
            editableBooking.customerFirstName +
            " " +
            editableBooking.customerLastName,
        },
        bill: {
          ...booking.bill,
          dailyRate: Number(editableBooking.dailyPrice),
          baseCost: Number(editableBooking.baseCost),
          extraHoursCost: Number(editableBooking.extraHoursCost),
          extraHoursRate: Number(editableBooking.extraHoursRate),
          totalCost:
            Number(editableBooking.baseCost) +
            Number(editableBooking.extraHoursCost) +
            (booking.bill?.insuranceCost || 0),
        },
      };
      await updateDoc(bookingRef, updatedBooking);
      setBooking(updatedBooking);
      setInitialBooking(updatedBooking);
      setEditMode(false);
    } catch (error) {
      console.error("Error saving booking details:", error);
    }
  };

  const isMoreThan24hBeforeStart = () => {
    if (!booking?.startDate) return false;
    const now = new Date().getTime();
    const start = new Date(booking.startDate).getTime();
    return start - now > 24 * 60 * 60 * 1000;
  };

  // Cancel (delete) booking
  const handleCancelBooking = async () => {
    if (!cancellationRequestedBy) {
      alert("Please select who requested the cancellation.");
      return;
    }

    const refundEligible =
      cancellationRequestedBy === "shop" ||
      (cancellationRequestedBy === "customer" &&
        (booking.policySnapshot?.cancellationInsurance
          ? isMoreThan24hBeforeStart()
          : shouldRefund));

    const confirmed = window.confirm(
      `Are you sure you want to cancel this booking?\nRefund: ${
        refundEligible ? "Yes" : "No"
      }`
    );

    if (!confirmed) return;

    try {
      await httpsCallable(
        functions,
        "cancelBooking"
      )({
        bookingId,
        cancelledBy: cancellationRequestedBy,
        cancellationNotes: cancellationNotes,
        shopId: shopSlug,
        manageToken: null,
      });
      navigate("/timeline");
    } catch (error) {
      console.error("Error cancelling booking:", error);
    }
  };

  if (loadingAuth || loading || !shopSlug) {
    return (
      <Box>
        <Container sx={{ mt: 6, mb: 8, textAlign: "center" }}>
          <CircularProgress />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading booking details...
          </Typography>
        </Container>
      </Box>
    );
  }

  if (!booking) {
    return (
      <Box>
        <Container sx={{ mt: 6, mb: 8, textAlign: "center" }}>
          <Typography variant="h6" align="center">
            Booking not found.
          </Typography>
        </Container>
      </Box>
    );
  }

  // For view mode, calculate duration and totalPrice for display using the original booking data
  const startTimeMs = new Date(booking.startDate).getTime();
  const endTimeMs = new Date(booking.endDate).getTime();
  const durationDays = (endTimeMs - startTimeMs) / (24 * 3600 * 1000);
  const computedTotalPrice = Number(booking.dailyPrice) * durationDays;

  // Also, calculate a human-friendly total time string
  const durationMs = endTimeMs - startTimeMs;
  const fullDays = Math.floor(durationMs / (24 * 3600 * 1000));
  const remainderHours = Math.floor(
    (durationMs % (24 * 3600 * 1000)) / (3600 * 1000)
  );
  let totalTimeString = `${fullDays} day${fullDays !== 1 ? "s" : ""}`;
  if (remainderHours > 0) {
    totalTimeString += ` and ${remainderHours} hour${
      remainderHours !== 1 ? "s" : ""
    }`;
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      {/* Top Navigation: Back to Timeline */}
      <Box sx={{ mb: 2 }}>
        <IconButton onClick={() => navigate("/timeline")}>
          <ArrowBackIosIcon /> Back
        </IconButton>
      </Box>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h4" align="left" gutterBottom>
          Booking Details
        </Typography>
        {editMode ? (
          <Box component="form">
            <Grid container spacing={2}>
              {/* Booking Title */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Booking Title"
                  name="title"
                  value={editableBooking.title}
                  onChange={handleInputChange}
                />
              </Grid>

              {/* Start & End Date */}
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Start Date & Time"
                  type="datetime-local"
                  name="startDate"
                  value={moment(editableBooking.startDate).format(
                    "YYYY-MM-DDTHH:mm"
                  )}
                  onChange={handleInputChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="End Date & Time"
                  type="datetime-local"
                  name="endDate"
                  value={moment(editableBooking.endDate).format(
                    "YYYY-MM-DDTHH:mm"
                  )}
                  onChange={handleInputChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              {/* First & Last Name */}
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  name="customerFirstName"
                  value={editableBooking.customerFirstName}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  name="customerLastName"
                  value={editableBooking.customerLastName}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  name="customerEmail"
                  value={editableBooking.customerEmail}
                  onChange={handleInputChange}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Daily Rate"
                  name="dailyPrice"
                  type="number"
                  value={editableBooking.dailyPrice}
                  onChange={handleDailyPriceChange}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Base Cost"
                  name="baseCost"
                  type="number"
                  value={editableBooking.baseCost}
                  onChange={handleBaseCostPriceChange}
                />
              </Grid>

              {/* Extra Hours Cost */}
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Extra Hours Cost"
                  name="extraHoursCost"
                  type="number"
                  value={editableBooking.extraHoursCost}
                  onChange={handleInputChange}
                />
              </Grid>

              {/* Total Price (editable, will recalc dailyRate + baseCost) */}
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Total Price"
                  name="totalPrice"
                  type="number"
                  value={editableBooking.totalPrice}
                  onChange={handleTotalPriceChange}
                />
              </Grid>

              {/* Deposit */}
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Deposit"
                  name="deposit"
                  type="number"
                  value={editableBooking.deposit}
                  onChange={handleInputChange}
                />
              </Grid>

              {/* Payment Method */}
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Payment Method"
                  name="paymentMethod"
                  value={editableBooking.paymentMethod}
                  onChange={handleInputChange}
                />
              </Grid>

              {/* Paid Status */}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={editableBooking.paid}
                      onChange={(e) =>
                        setEditableBooking((prev) => ({
                          ...prev,
                          paid: e.target.checked,
                        }))
                      }
                    />
                  }
                  label="Paid"
                />
              </Grid>
            </Grid>

            {/* Save/Cancel Buttons */}
            <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
              <Button variant="contained" onClick={handleSave}>
                Save
              </Button>
              <Button variant="outlined" onClick={() => handleCancelEdit}>
                Cancel
              </Button>
            </Box>
          </Box>
        ) : (
          <Box>
            <Typography variant="h6">
              <strong>Motorbike:</strong>{" "}
              {vehicle ? `${vehicle.model} - ${vehicle.plateNr}` : "N/A"}
            </Typography>
            <Typography variant="body1" sx={{ mt: 1 }}>
              <strong>Start Date & Time:</strong>{" "}
              {moment(booking.startDate).format("LLLL")}
            </Typography>
            <Typography variant="body1">
              <strong>Return Date & Time:</strong>{" "}
              {moment(booking.endDate).format("LLLL")}
            </Typography>
            <Typography variant="body1">
              <strong>Total Time:</strong> {totalTimeString}
            </Typography>
            {booking.policySnapshot?.cancellationInsurance && (
              <Typography variant="body1" sx={{ mt: 1 }}>
                <strong>Cancellation policy:</strong> Booking amount (₱
                {booking.bill?.baseCost + booking.bill?.extraHoursCost}) is
                refundable until 24h prior to pick up time.
              </Typography>
            )}
            {!booking.policySnapshot?.cancellationInsurance && (
              <Typography variant="body1" sx={{ mt: 1 }}>
                <strong>Cancellation policy:</strong> Non Refundable
              </Typography>
            )}
            <Typography variant="body1" sx={{ mt: 1 }}>
              <strong>Daily Rate:</strong> ₱
              {booking.bill?.dailyRate?.toFixed(2)}
            </Typography>
            <Typography variant="body1">
              <strong>Base Cost:</strong> ₱{booking.bill?.baseCost?.toFixed(2)}
            </Typography>
            <Typography variant="body1" sx={{ mt: 1 }}>
              <strong>Extra Hours:</strong> {booking.bill?.paidExtraHours}
            </Typography>
            <Typography variant="body1">
              <strong>Extra Hours Rate:</strong> ₱
              {booking.bill?.extraHoursRate?.toFixed(2)}
            </Typography>
            <Typography variant="body1">
              <strong>Extra Hours Cost:</strong> ₱
              {booking.bill?.extraHoursCost?.toFixed(2)}
            </Typography>
            {booking.policySnapshot?.cancellationInsurance && (
              <Typography variant="body1" sx={{ mt: 1 }}>
                <strong>Cancellation insurance:</strong> Selected
              </Typography>
            )}
            {!booking.policySnapshot?.cancellationInsurance && (
              <Typography variant="body1" sx={{ mt: 1 }}>
                <strong>Cancellation insurance:</strong> Rejected
              </Typography>
            )}
            <Typography variant="body1">
              <strong>Insurance cost:</strong> ₱
              {booking.bill?.insuranceCost?.toFixed(2)}
            </Typography>

            <Typography variant="body1" sx={{ mt: 1 }}>
              <strong>Total Price:</strong> ₱
              {booking.bill?.totalCost?.toFixed(2)}
            </Typography>

            <Typography sx={{ mt: 1 }}>
              <strong>Booking Method:</strong> {booking.source?.bookingMethod}
            </Typography>
            <Typography variant="body1">
              <strong>Customer Name:</strong> {booking.customer?.fullName}
            </Typography>
            <Typography>
              <strong>Contact:</strong> {booking.customer?.email}
            </Typography>

            {booking.extras && booking.extras.length > 0 && (
              <Typography variant="body1" sx={{ mt: 1 }}>
                <strong>Selected Extras:</strong>{" "}
                {booking.extras.map((extra) => extra.name).join(", ")}
              </Typography>
            )}
            <Typography variant="body1" sx={{ mt: 1 }}>
              <strong>Deposit (Cash):</strong> ₱
              {Number(booking.deposit).toFixed(2)}
            </Typography>
            {booking.payments && booking.payments.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body1">
                  <strong>Payments Made:</strong>
                </Typography>
                {booking.payments.map((payment, index) => (
                  <Typography key={index} variant="body2">
                    {moment(payment.date).format("LLL")}: ₱
                    {Number(payment.amount).toFixed(2)}
                  </Typography>
                ))}
              </Box>
            )}
            {booking.status !== "cancelled" ? (
              <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
                <Button variant="contained" onClick={() => setEditMode(true)}>
                  Edit Booking
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => setShowCancelForm((prev) => !prev)}
                >
                  Cancel Booking
                </Button>
              </Box>
            ) : (
              <Box
                sx={{
                  mt: 2,
                  backgroundColor: "error.main",
                  color: "white",
                  px: 6,
                  py: 1,
                  borderRadius: 0,
                  display: "inline-block",
                  fontWeight: 500,
                }}
              >
                Cancelled
              </Box>
            )}

            {showCancelForm && (
              <Box
                sx={{
                  mt: 2,
                  border: "1px solid #ccc",
                  p: 2,
                  borderRadius: 2,
                }}
              >
                <Typography variant="h6" gutterBottom>
                  Cancellation Details
                </Typography>

                {/* Who requested the cancellation */}
                <TextField
                  select
                  fullWidth
                  label="Who requested the cancellation?"
                  value={cancellationRequestedBy}
                  onChange={(e) => setCancellationRequestedBy(e.target.value)}
                  sx={{ mb: 2 }}
                >
                  <MenuItem value="customer">Customer</MenuItem>
                  <MenuItem value="shop">Shop</MenuItem>
                </TextField>

                {/* Refund logic */}
                {cancellationRequestedBy === "customer" && (
                  <>
                    {booking.policySnapshot?.cancellationInsurance ? (
                      isMoreThan24hBeforeStart() ? (
                        <Typography color="success.main" sx={{ mb: 2 }}>
                          ✅ Customer has the right to a full refund of the
                          booking amount (₱
                          {booking.bill.baseCost + booking.bill.extraHoursCost})
                          until 24 hours prior to pick-up.
                        </Typography>
                      ) : (
                        <Typography color="warning.main" sx={{ mb: 2 }}>
                          ❌ Less than 24 hours before pick-up. Cancellation
                          insurance doesn't apply.
                        </Typography>
                      )
                    ) : (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={shouldRefund}
                            onChange={(e) => setShouldRefund(e.target.checked)}
                          />
                        }
                        label="Exceptionally refund booking amount (customer did not purchase cancellation insurance)"
                        sx={{ mb: 2 }}
                      />
                    )}
                  </>
                )}

                {cancellationRequestedBy === "shop" && (
                  <Typography color="success.main" sx={{ mb: 2 }}>
                    ✅ Full refund will be issued to the customer
                    {booking.policySnapshot.cancellationInsurance &&
                      ", including insurance cost"}{" "}
                    (₱{booking.bill.totalCost}).
                  </Typography>
                )}

                {/* Notes */}
                <TextField
                  label="Cancellation Notes"
                  fullWidth
                  multiline
                  minRows={3}
                  value={cancellationNotes}
                  onChange={(e) => setCancellationNotes(e.target.value)}
                  sx={{ mb: 2 }}
                />

                {/* Confirm cancellation button */}
                <Button
                  variant="contained"
                  color="error"
                  onClick={() => handleCancelBooking()}
                >
                  Confirm Cancellation
                </Button>
              </Box>
            )}
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default BookingDetail;
